import { useEffect, useState } from "react"
import ProgressBar from "@ramonak/react-progress-bar"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FiBookOpen, FiClock, FiPlay, FiCheckCircle, FiAward, FiLock } from "react-icons/fi"

import { getUserEnrolledCourses } from "../../../services/operations/profileAPI"

export default function EnrolledCourses() {
  const { token } = useSelector((state) => state.auth)
  const navigate = useNavigate()
  const [enrolledCourses, setEnrolledCourses] = useState(null)

  useEffect(() => {
    ;(async () => {
      try {
        const res = await getUserEnrolledCourses(token)
        setEnrolledCourses(res)
      } catch {
        setEnrolledCourses([])
      }
    })()
  }, [])

  if (!enrolledCourses) {
    return (
      <div className="grid min-h-[calc(100vh-3.5rem)] place-items-center">
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div className="px-4 md:px-0 pb-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-richblack-5">My Learning</h1>
        <p className="mt-1 text-sm text-richblack-400">
          {enrolledCourses.length} course{enrolledCourses.length !== 1 ? "s" : ""} enrolled
        </p>
      </div>

      {!enrolledCourses.length ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-richblack-700 bg-richblack-800 py-20 text-center">
          <FiBookOpen className="mb-4 text-5xl text-richblack-600" />
          <p className="text-lg font-semibold text-richblack-100">No courses yet</p>
          <p className="mt-1 text-sm text-richblack-400">Enroll in a course to start learning</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {enrolledCourses.map((course, i) => {
            const pct = course.progressPercentage || 0
            const isComplete = pct === 100
            const hasStarted = pct > 0

            return (
              <div
                key={i}
                className="group flex flex-col overflow-hidden rounded-2xl border border-richblack-700 bg-richblack-800 transition-all duration-200 hover:border-richblack-600 hover:shadow-[0_0_28px_rgba(255,214,10,0.07)]"
              >
                {/* Thumbnail */}
                <div className="relative overflow-hidden">
                  <img
                    src={course.thumbnail}
                    alt={course.courseName}
                    className="h-[170px] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {isComplete && (
                    <div className="absolute inset-0 flex items-center justify-center bg-richblack-900/60">
                      <FiCheckCircle className="text-4xl text-caribbeangreen-300 drop-shadow" />
                    </div>
                  )}
                  {/* progress strip at bottom of image */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-richblack-700">
                    <div
                      className="h-full bg-yellow-50 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Body */}
                <div className="flex flex-1 flex-col gap-3 p-4">
                  <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-richblack-5">
                    {course.courseName}
                  </h3>

                  {course.totalDuration && (
                    <div className="flex items-center gap-1.5 text-xs text-richblack-400">
                      <FiClock size={12} />
                      {course.totalDuration}
                    </div>
                  )}

                  {/* Progress */}
                  <div className="mt-auto flex flex-col gap-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-richblack-400">Progress</span>
                      <span className="font-semibold text-richblack-100">{pct}%</span>
                    </div>
                    <ProgressBar
                      completed={pct}
                      height="5px"
                      isLabelVisible={false}
                      bgColor={isComplete ? "#47A992" : "#FFD60A"}
                      baseBgColor="#2C333F"
                      borderRadius="4px"
                    />
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() =>
                      navigate(
                        `/view-course/${course?._id}/section/${course.courseContent?.[0]?._id}/sub-section/${course.courseContent?.[0]?.subSection?.[0]?._id}`
                      )
                    }
                    className="mt-1 flex items-center justify-center gap-2 rounded-lg bg-yellow-50 py-2 text-xs font-semibold text-richblack-900 transition-all duration-150 hover:bg-yellow-100 active:scale-95"
                  >
                    <FiPlay size={12} />
                    {isComplete ? "Review Course" : hasStarted ? "Continue Learning" : "Start Learning"}
                  </button>

                  {/* Assessment CTA */}
                  {pct >= 80 ? (
                    <button
                      onClick={() => navigate(`/dashboard/assessment/${course._id}`)}
                      className="flex items-center justify-center gap-2 rounded-lg border border-caribbeangreen-300/40 bg-caribbeangreen-300/10 py-2 text-xs font-semibold text-caribbeangreen-300 transition-all duration-150 hover:bg-caribbeangreen-300/20 active:scale-95"
                    >
                      <FiAward size={12} />
                      Take Assessment
                    </button>
                  ) : (
                    <div className="flex items-center justify-center gap-2 rounded-lg border border-richblack-600 py-2 text-xs font-medium text-richblack-500 cursor-not-allowed">
                      <FiLock size={12} />
                      Assessment (complete 80% to unlock)
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
